<template>
  <div class="p-0">
    <div v-if="loading" class="py-10">
      <Loading />
    </div>
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
      <button type="button" @click="fetchRequest" class="ml-2 underline font-semibold">Reintentar</button>
    </div>
    <div v-else>
      <div class="mb-5">
        <router-link to="/leave/inbox" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900">
          <i class="fa-solid fa-arrow-left mr-2"></i>
          Volver a bandeja
        </router-link>
      </div>

      <header class="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-5">
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="text-2xl font-bold text-gray-900">Solicitud</h1>
              <span class="px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(request.status)">
                {{ statusLabel(request.status) }}
              </span>
            </div>
            <p class="mt-1 text-sm text-gray-600">{{ requestTitle(request) }}</p>
            <p class="mt-2 text-sm font-semibold text-gray-900">{{ requestTeacherName(request) }}</p>
            <p v-if="requestTeacherMeta(request)" class="text-xs text-gray-500">{{ requestTeacherMeta(request) }}</p>
          </div>

          <div v-if="canReview" class="flex flex-wrap gap-2">
            <button type="button" @click="openModal('observe')" :disabled="saving" class="warning-button">
              <i class="fa-regular fa-eye mr-2"></i>
              Observar
            </button>
            <button type="button" @click="openModal('reject')" :disabled="saving" class="danger-outline-button">
              <i class="fa-solid fa-xmark mr-2"></i>
              Rechazar
            </button>
            <button type="button" @click="openApprove" :disabled="saving || !canApprove" class="primary-button disabled:opacity-50">
              <i class="fa-regular fa-circle-check mr-2"></i>
              Aprobar
            </button>
          </div>
        </div>
      </header>

      <div v-if="success" class="mb-5 bg-green-50 border border-green-100 text-green-700 rounded-lg px-4 py-3">
        {{ success }}
      </div>

      <section v-if="canReview" class="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 class="font-bold text-gray-900">Checklist previo a aprobar</h2>
            <p class="text-sm text-gray-500">El backend vuelve a validar requisitos, documentacion, cupo y superposiciones.</p>
          </div>
          <button v-if="canCancel" type="button" @click="openModal('cancel')" class="secondary-button">
            Cancelar solicitud
          </button>
        </div>
        <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <article class="check-card" :class="requirementsOk ? 'check-ok' : 'check-warn'">
            <i class="fa-regular fa-circle-check mr-2"></i>
            Requisitos: {{ requirementsOk ? 'OK' : `${blockingRequirements.length} pendientes` }}
          </article>
          <article class="check-card" :class="documentsOk ? 'check-ok' : 'check-warn'">
            <i class="fa-solid fa-paperclip mr-2"></i>
            Documentacion: {{ documentsOk ? 'OK' : 'pendiente' }}
          </article>
          <article class="check-card" :class="quotaOk ? 'check-ok' : 'check-warn'">
            <i class="fa-solid fa-chart-simple mr-2"></i>
            Cupo: {{ quotaOk ? 'disponible' : 'revisar' }}
          </article>
        </div>
        <p v-if="!canApprove" class="mt-3 text-sm text-orange-700">
          No se puede aprobar hasta resolver los items pendientes.
        </p>
      </section>

      <nav class="module-tabs">
        <button v-for="tab in tabs" :key="tab.key" type="button" @click="activeTab = tab.key" class="module-tab" :class="activeTab === tab.key ? 'module-tab-active' : ''">
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="activeTab === 'summary'" class="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div class="xl:col-span-2 space-y-5">
          <article class="panel">
            <h2 class="panel-title">Resumen</h2>
            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div class="info-box">
                <div class="info-label">Docente</div>
                <div class="info-value">{{ requestTeacherName(request) }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Articulo</div>
                <div class="info-value">{{ requestTitle(request) }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Fechas</div>
                <div class="info-value">{{ dateRange }}</div>
              </div>
              <div class="info-box">
                <div class="info-label">Duracion</div>
                <div class="info-value">{{ durationText }}</div>
              </div>
            </div>
            <div class="mt-4 border-t border-gray-100 pt-4">
              <div class="info-label">Motivo / razon</div>
              <p class="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{{ request.reason || '-' }}</p>
            </div>
          </article>

          <article class="panel">
            <h2 class="panel-title">Historial de la solicitud</h2>
            <div v-if="history.length === 0" class="mt-3 text-sm text-gray-500">No hay historial disponible.</div>
            <div v-else class="mt-4 space-y-4">
              <div v-for="event in history" :key="event.id || event.created_at || event.status" class="flex gap-3">
                <span class="mt-1 w-3 h-3 rounded-full bg-indigo-800 flex-shrink-0"></span>
                <div>
                  <div class="text-sm font-semibold text-gray-900">{{ event.title || event.label || statusLabel(event.status) || 'Movimiento' }}</div>
                  <div class="text-xs text-gray-500">{{ formatDateTime(event.created_at || event.date) }}</div>
                  <p v-if="event.comment || event.observation || event.reason" class="mt-1 text-xs text-gray-600 bg-gray-50 rounded p-2">
                    {{ event.comment || event.observation || event.reason }}
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>

        <aside class="space-y-5">
          <article class="panel">
            <h2 class="panel-title">Cupo del docente</h2>
            <p class="mt-2 text-sm text-gray-600">{{ availability?.message || 'Consulta de disponibilidad segun fecha de inicio.' }}</p>
            <router-link :to="balanceLink" class="mt-4 inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900">
              Ver cupos del docente <i class="fa-solid fa-arrow-right ml-2"></i>
            </router-link>
          </article>
          <article class="bg-indigo-900 text-indigo-50 rounded-xl p-5 shadow-sm">
            <h2 class="font-semibold">Resumen de decision</h2>
            <p class="mt-2 text-sm text-indigo-100">{{ decisionSummary }}</p>
          </article>
        </aside>
      </section>

      <section v-else-if="activeTab === 'requirements'" class="panel">
        <h2 class="panel-title">Requisitos de la solicitud</h2>
        <p class="mt-1 text-sm text-gray-500">Marca cada condicion despues de verificarla.</p>
        <div v-if="requirements.length === 0" class="mt-5 border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
          No hay requisitos informados para esta solicitud.
        </div>
        <div v-else class="mt-5 divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          <article v-for="requirement in requirements" :key="requirement.id || requirement.code" class="p-4 bg-white">
            <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div>
                <div class="font-semibold text-gray-900">{{ requirement.name || requirement.code || 'Requisito' }}</div>
                <p class="text-sm text-gray-500">{{ requirement.requirement_name || requirement.requirement_type || 'Validacion administrativa' }}</p>
                <span class="mt-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full border" :class="requirementStatusClass(requirement.status, requirement.passed)">
                  {{ requirementStatusLabel(requirement.status, requirement.passed) }}
                </span>
                <span v-if="requirement.is_required" class="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700">Obligatorio</span>
              </div>
              <div v-if="canReview" class="flex flex-wrap gap-2">
                <button type="button" @click="markRequirement(requirement, 'pass')" class="small-success-button">Cumplido</button>
                <button type="button" @click="markRequirement(requirement, 'fail')" class="small-danger-button">Incumplido</button>
                <button type="button" @click="markRequirement(requirement, 'notApplicable')" class="small-secondary-button">No aplica</button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-else class="panel">
        <h2 class="panel-title">Documentacion adjunta</h2>
        <p class="mt-1 text-sm text-gray-500">Revisa y aproba cada archivo. Si rechazas uno, la solicitud puede quedar observada.</p>
        <div v-if="documents.length === 0" class="mt-5 border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
          No hay documentos adjuntos.
        </div>
        <div v-else class="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <article v-for="document in documents" :key="document.id || document.document_id || document.file_name" class="border border-gray-200 rounded-xl p-4 bg-white">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="font-semibold text-gray-900 truncate">{{ documentName(document) }}</div>
                <div class="text-xs text-gray-500">{{ document.document_type || document.type || 'General' }}</div>
              </div>
              <span class="px-2 py-1 text-xs font-semibold rounded-full border" :class="documentStatusClass(document.status)">
                {{ documentStatusLabel(document.status) }}
              </span>
            </div>
            <p v-if="document.rejection_reason || document.reason" class="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-2">
              Motivo: {{ document.rejection_reason || document.reason }}
            </p>
            <div class="mt-4 flex flex-wrap gap-2">
              <button type="button" @click="openDocumentViewer(document)" class="small-secondary-button">
                <i class="fa-regular fa-eye mr-2"></i>
                Ver
              </button>
              <button v-if="canReview" type="button" @click="approveDocument(document)" class="small-success-button">
                Aprobar doc
              </button>
              <button v-if="canReview" type="button" @click="openDocumentReject(document)" class="small-danger-button">
                Rechazar doc
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div v-if="modal.type" class="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-bold text-gray-900">{{ modalTitle }}</h2>
            <p class="mt-1 text-sm text-gray-600">{{ modalHelp }}</p>
          </div>
          <button type="button" @click="closeModal" class="text-gray-400 hover:text-gray-700">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5">
          <div v-if="modal.type === 'approve'" class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
            <div class="font-semibold text-gray-900">{{ requestTeacherName(request) }}</div>
            <div class="text-gray-600">{{ requestTitle(request) }} · {{ dateRange }}</div>
          </div>
          <label class="label mt-4">{{ modalLabel }}</label>
          <textarea v-model="modal.text" rows="4" class="field" :placeholder="modalPlaceholder"></textarea>
        </div>
        <div class="px-5 py-4 bg-gray-50 flex justify-end gap-2">
          <button type="button" @click="closeModal" class="secondary-button">Cancelar</button>
          <button type="button" @click="confirmModal" :disabled="saving || modalRequiresText && !modal.text.trim()" class="primary-button disabled:opacity-50">
            {{ saving ? 'Procesando...' : modalConfirmText }}
          </button>
        </div>
      </div>
    </div>

    <LeaveDocumentViewer
      v-if="viewerDocument"
      :document="viewerDocument"
      @close="closeDocumentViewer"
    />
  </div>
</template>

<script>
import LeaveDocumentViewer from '@/components/leave/LeaveDocumentViewer.vue';
import Loading from '@/components/commons/Loading.vue';
import {
  leaveAvailabilityApi,
  leaveRequestDocumentsApi,
  leaveRequestRequirementsApi,
  leaveRequestsApi,
} from '@/service/leave.service';
import {
  documentStatusClass,
  documentStatusLabel,
  formatDate,
  formatDateTime,
  requestDocuments,
  requestHistory,
  requestRequirements,
  requestTeacherMeta,
  requestTeacherName,
  requestTitle,
  requirementStatusClass,
  requirementStatusLabel,
  statusClass,
  statusLabel,
  unwrapData,
  unwrapList,
} from '@/utils/leave';

export default {
  name: 'LeaveReviewView',
  components: { LeaveDocumentViewer, Loading },
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      success: '',
      request: {},
      requirements: [],
      availability: null,
      activeTab: this.$route.query.tab || 'summary',
      viewerDocument: null,
      modal: {
        type: '',
        text: '',
        document: null,
      },
    };
  },
  computed: {
    requestId() {
      return this.$route.params.id;
    },
    documents() {
      return requestDocuments(this.request);
    },
    history() {
      return requestHistory(this.request);
    },
    tabs() {
      return [
        { key: 'summary', label: 'Resumen' },
        { key: 'requirements', label: `Requisitos (${this.requirementsPendingCount}/${this.requirements.length})` },
        { key: 'documents', label: `Documentos (${this.documentsPendingCount})` },
      ];
    },
    canReview() {
      return ['draft', 'pending', 'observed', 'suspended'].includes(this.request.status);
    },
    canCancel() {
      return ['draft', 'pending', 'observed'].includes(this.request.status);
    },
    blockingRequirements() {
      return this.requirements.filter((requirement) => {
        const status = requirement.status || (requirement.passed ? 'passed' : 'pending');
        return requirement.is_required !== false && ['pending', 'failed'].includes(status);
      });
    },
    requirementsPendingCount() {
      return this.requirements.filter((requirement) => {
        const status = requirement.status || (requirement.passed ? 'passed' : 'pending');
        return ['pending', 'failed'].includes(status);
      }).length;
    },
    documentsPendingCount() {
      return this.documents.filter((document) => !document.status || document.status === 'pending').length;
    },
    requirementsOk() {
      return this.blockingRequirements.length === 0;
    },
    documentsOk() {
      if (this.documents.length === 0) {
        return !this.request.requires_documentation;
      }
      return this.documents.every((document) => document.status === 'approved');
    },
    quotaOk() {
      return this.availability ? Boolean(this.availability.can_request) : true;
    },
    canApprove() {
      return this.canReview && this.requirementsOk && this.documentsOk && this.quotaOk;
    },
    dateRange() {
      const start = formatDate(this.request.start_date);
      const end = formatDate(this.request.end_date);
      return start === end ? start : `${start} -> ${end}`;
    },
    durationText() {
      const days = this.request.requested_days ?? this.request.total_days
        ?? (Array.isArray(this.request.days) ? this.request.days.length : this.request.days);
      if (days) return `${days} ${Number(days) === 1 ? 'dia' : 'dias'}`;
      if (this.request.requested_hours) return `${this.request.requested_hours} horas`;
      return '-';
    },
    balanceLink() {
      return {
        path: '/leave/balances',
        query: {
          user_id: this.request.user_id,
          date: this.request.start_date,
        },
      };
    },
    decisionSummary() {
      if (this.canApprove) return 'La solicitud cumple los criterios visibles y puede aprobarse.';
      return 'Hay items pendientes antes de aprobar. Revisa requisitos, documentos o cupo.';
    },
    modalTitle() {
      return {
        approve: 'Aprobar solicitud',
        reject: 'Rechazar solicitud',
        observe: 'Observar solicitud',
        cancel: 'Cancelar solicitud',
        rejectDocument: 'Rechazar documento',
      }[this.modal.type] || '';
    },
    modalHelp() {
      return {
        approve: 'Confirma formalmente la aprobacion. La nota interna es opcional.',
        reject: 'Indica el motivo de rechazo que recibira el docente.',
        observe: 'Indica que debe corregir o completar el docente.',
        cancel: 'Indica el motivo administrativo de cancelacion.',
        rejectDocument: 'El documento quedara rechazado y la solicitud puede volver a observada.',
      }[this.modal.type] || '';
    },
    modalLabel() {
      return this.modal.type === 'approve' ? 'Nota interna (opcional)' : 'Motivo / observacion *';
    },
    modalPlaceholder() {
      return this.modal.type === 'approve'
        ? 'Observaciones adicionales para el expediente...'
        : 'Describe el motivo de forma clara...';
    },
    modalConfirmText() {
      return {
        approve: 'Confirmar aprobacion',
        reject: 'Rechazar',
        observe: 'Observar',
        cancel: 'Confirmar cancelacion',
        rejectDocument: 'Rechazar documento',
      }[this.modal.type] || 'Confirmar';
    },
    modalRequiresText() {
      return this.modal.type !== 'approve';
    },
  },
  created() {
    this.fetchRequest();
  },
  methods: {
    documentStatusClass,
    documentStatusLabel,
    formatDateTime,
    requestTeacherName,
    requestTeacherMeta,
    requestTitle,
    requirementStatusClass,
    requirementStatusLabel,
    statusClass,
    statusLabel,
    async fetchRequest() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveRequestsApi.get(this.requestId);
        this.request = unwrapData(response);
        this.requirements = requestRequirements(this.request);
        await Promise.all([this.fetchRequirements(), this.fetchAvailability()]);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar la solicitud.';
      } finally {
        this.loading = false;
      }
    },
    async fetchRequirements() {
      try {
        const response = await leaveRequestsApi.requirements(this.requestId);
        const requirements = unwrapList(response);
        if (requirements.length) this.requirements = requirements;
      } catch {
        // El detalle puede incluir requisitos; si el endpoint falla, dejamos ese dato.
      }
    },
    async fetchAvailability() {
      if (!this.request.user_id || !this.request.leave_article_id || !this.request.start_date) return;
      try {
        const response = await leaveAvailabilityApi.get(this.request.user_id, {
          article_id: this.request.leave_article_id,
          date: this.request.start_date,
        });
        this.availability = unwrapData(response);
      } catch {
        this.availability = null;
      }
    },
    openApprove() {
      if (!this.canApprove) {
        this.activeTab = !this.requirementsOk ? 'requirements' : 'documents';
        return;
      }
      this.openModal('approve');
    },
    openModal(type) {
      this.modal = { type, text: '', document: null };
    },
    openDocumentReject(document) {
      this.modal = { type: 'rejectDocument', text: '', document };
    },
    closeModal() {
      this.modal = { type: '', text: '', document: null };
    },
    async confirmModal() {
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        if (this.modal.type === 'approve') {
          await leaveRequestsApi.approve(this.requestId, { note: this.modal.text });
          this.success = 'Solicitud aprobada.';
        } else if (this.modal.type === 'reject') {
          await leaveRequestsApi.reject(this.requestId, this.modal.text);
          this.success = 'Solicitud rechazada.';
        } else if (this.modal.type === 'observe') {
          await leaveRequestsApi.observe(this.requestId, this.modal.text);
          this.success = 'Solicitud observada.';
        } else if (this.modal.type === 'cancel') {
          await leaveRequestsApi.cancel(this.requestId, this.modal.text);
          this.success = 'Solicitud cancelada.';
        } else if (this.modal.type === 'rejectDocument') {
          const id = this.modal.document?.id || this.modal.document?.document_id;
          await leaveRequestDocumentsApi.reject(id, this.modal.text);
          this.success = 'Documento rechazado.';
        }
        this.closeModal();
        await this.fetchRequest();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo completar la accion.';
      } finally {
        this.saving = false;
      }
    },
    async markRequirement(requirement, action) {
      const id = requirement.id || requirement.requirement_id;
      if (!id) return;
      this.saving = true;
      this.error = '';
      try {
        await leaveRequestRequirementsApi[action](id, {});
        this.success = 'Requisito actualizado.';
        await this.fetchRequest();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo actualizar el requisito.';
      } finally {
        this.saving = false;
      }
    },
    async approveDocument(document) {
      const id = document.id || document.document_id;
      if (!id) return;
      this.saving = true;
      this.error = '';
      try {
        await leaveRequestDocumentsApi.approve(id);
        this.success = 'Documento aprobado.';
        await this.fetchRequest();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo aprobar el documento.';
      } finally {
        this.saving = false;
      }
    },
    openDocumentViewer(document) {
      this.viewerDocument = document;
    },
    closeDocumentViewer() {
      this.viewerDocument = null;
    },
    documentName(document) {
      return document.file_name || document.name || document.original_name || 'Documento';
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.panel {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-5;
}
.panel-title {
  @apply font-bold text-gray-900;
}
.info-box {
  @apply bg-gray-50 border border-gray-100 rounded-md p-3;
}
.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500;
}
.info-value {
  @apply mt-1 font-semibold text-gray-900;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.warning-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-semibold bg-orange-50 border border-orange-200 text-orange-700 rounded-md hover:bg-orange-100;
}
.danger-outline-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-semibold bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50;
}
.small-secondary-button {
  @apply inline-flex items-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200;
}
.small-success-button {
  @apply inline-flex items-center px-3 py-2 text-xs font-semibold text-green-700 bg-green-50 rounded-md hover:bg-green-100;
}
.small-danger-button {
  @apply inline-flex items-center px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 rounded-md hover:bg-red-100;
}
.check-card {
  @apply rounded-lg border px-3 py-2 text-sm font-semibold;
}
.check-ok {
  @apply bg-green-50 border-green-100 text-green-700;
}
.check-warn {
  @apply bg-orange-50 border-orange-100 text-orange-700;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * badge color combos below that the retrofit doesn't cover for anyone.
 */
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .label {
  @apply text-gray-400;
}
.dark .panel {
  @apply bg-gray-800 border-gray-700;
}
.dark .panel-title {
  @apply text-gray-100;
}
.dark .info-box {
  @apply bg-gray-900 border-gray-700;
}
.dark .info-label {
  @apply text-gray-400;
}
.dark .info-value {
  @apply text-gray-100;
}
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}
.dark .secondary-button {
  @apply bg-gray-800 border-gray-600 text-gray-200;
}
.dark .secondary-button:hover {
  @apply bg-gray-700;
}
.dark .warning-button {
  background-color: rgba(249, 115, 22, 0.15);
  border-color: rgba(249, 115, 22, 0.35);
  color: theme('colors.orange.300');
}
.dark .warning-button:hover {
  background-color: rgba(249, 115, 22, 0.25);
}
.dark .danger-outline-button {
  @apply bg-gray-800 text-red-300;
  border-color: rgba(239, 68, 68, 0.4);
}
.dark .danger-outline-button:hover {
  background-color: rgba(239, 68, 68, 0.15);
}
.dark .small-secondary-button {
  @apply bg-gray-700 text-gray-200;
}
.dark .small-secondary-button:hover {
  @apply bg-gray-600;
}
.dark .small-success-button {
  background-color: rgba(34, 197, 94, 0.15);
  color: theme('colors.green.300');
}
.dark .small-success-button:hover {
  background-color: rgba(34, 197, 94, 0.25);
}
.dark .small-danger-button {
  background-color: rgba(239, 68, 68, 0.15);
  color: theme('colors.red.300');
}
.dark .small-danger-button:hover {
  background-color: rgba(239, 68, 68, 0.25);
}
.dark .check-ok {
  background-color: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.35);
  color: theme('colors.green.300');
}
.dark .check-warn {
  background-color: rgba(249, 115, 22, 0.15);
  border-color: rgba(249, 115, 22, 0.35);
  color: theme('colors.orange.300');
}

/* Status badge colors (statusClass/requirementStatusClass/documentStatusClass)
   that the global dark-mode retrofit doesn't cover. */
.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .border-red-100,
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-100,
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-100,
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .hover\:text-indigo-900:hover {
  color: theme('colors.indigo.200');
}
</style>
